import { useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";
import {
  UploadSimple, FilePdf, FileDoc, FileText, Trash, Star,
  PaperclipHorizontal, FolderOpen, Sparkle, EnvelopeSimple, X, MapPin,
} from "@phosphor-icons/react";
import { toast } from "sonner";
import { getToken } from "@/lib/authToken";
import FolderBrowser from "./FolderBrowser";
const ICONS = {
  "application/pdf": { Icon: FilePdf, color: "text-status-error-fg" },
  "application/msword": { Icon: FileDoc, color: "text-status-info-fg" },
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document": { Icon: FileDoc, color: "text-status-info-fg" },
};

const CATEGORIA_LABEL = {
  presupuesto: "Presupuesto",
  factura: "Factura",
  comunicacion: "Comunicación",
  informe_tecnico: "Informe técnico",
  fotografia: "Fotografía",
  documentacion_poliza: "Doc. póliza",
  otro: "Otros",
};
const CATEGORIA_COLOR = {
  presupuesto: "bg-emerald-50 text-emerald-700 border-emerald-200",
  factura: "bg-violet-50 text-violet-700 border-violet-200",
  comunicacion: "bg-sky-50 text-sky-700 border-sky-200",
  informe_tecnico: "bg-indigo-50 text-indigo-700 border-indigo-200",
  fotografia: "bg-pink-50 text-pink-700 border-pink-200",
  documentacion_poliza: "bg-amber-50 text-amber-700 border-amber-200",
  otro: "bg-slate-50 text-slate-600 border-slate-200",
};
const fmtSize = (b) => (b > 1024 * 1024 ? (b / 1024 / 1024).toFixed(2) + " MB" : (b / 1024).toFixed(0) + " KB");
const fmtDate = (iso) => new Date(iso).toLocaleString("es-ES", { day: "2-digit", month: "2-digit", year: "2-digit", hour: "2-digit", minute: "2-digit" });

export default function DocsTab({ expedienteId, onCountChange, onSendEmail }) {
  const [items, setItems] = useState([]);
  const [drag, setDrag] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [selected, setSelected] = useState(new Set());
  const inputRef = useRef(null);
  const token = getToken();

  const toggleSelect = (id) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };
  const clearSelection = () => setSelected(new Set());
  const selectedItems = items.filter((i) => selected.has(i.id));
  const selectedSize = selectedItems.reduce((a, i) => a + (i.size_bytes || 0), 0);

  const sendSelected = () => {
    if (selectedItems.length === 0) {
      toast.error("Selecciona al menos un documento");
      return;
    }
    if (selectedSize > 25 * 1024 * 1024) {
      toast.error(`Tamaño total ${fmtSize(selectedSize)} supera el límite de 25 MB`);
      return;
    }
    onSendEmail?.({ attachmentIds: Array.from(selected), files: selectedItems });
    clearSelection();
  };

  const load = async () => {
    const r = await api.get(`/expedientes/${expedienteId}/attachments?kind=docs`);
    setItems(r.data);
    onCountChange?.();
  };

  // Carpetas personalizadas creadas por el usuario (lista de paths)
  const [customFolders, setCustomFolders] = useState([]);
  const loadFolders = async () => {
    try {
      const r = await api.get(`/expedientes/${expedienteId}/folders?kind=doc`);
      setCustomFolders(r.data?.folders || []);
    } catch { /* ignore */ }
  };
  useEffect(() => { loadFolders(); /* eslint-disable-next-line */ }, [expedienteId]);

  const moveItem = async (attId, folder) => {
    await api.patch(`/expedientes/${expedienteId}/attachments/${attId}`, { folder });
    load();
    loadFolders();
  };

  // Carpetas "sistema" que se gestionan con la vista de secciones existente.
  //  - "informe"            → carpeta del Anexo del informe (sección dedicada arriba).
  //  - "emails indexados"   → copia automática de emails enviados.
  //  - "informe_*"          → informes generados (PDF/DOCX final y preliminar),
  //                           que se muestran en "Documentos generales" con el
  //                           log "Generado el DD/MM/AAAA a las HH:MM".
  const SYSTEM_FOLDERS_LC = new Set(["informe", "emails indexados"]);
  const isSystemFolder = (p) => {
    const lc = (p || "").toLowerCase();
    if (!lc) return false;
    if (SYSTEM_FOLDERS_LC.has(lc)) return true;
    return lc.startsWith("informe_");
  };
  // Custom = todo path que no sea sistema.
  const userCustomFolders = customFolders.filter((p) => !isSystemFolder(p));
  // ¿Hay archivos en carpetas custom?
  const hasUserItemsInCustom = items.some(
    (i) => i.folder && !isSystemFolder(i.folder)
  );
  const showFolderBrowser = userCustomFolders.length > 0 || hasUserItemsInCustom;

  /**
   * Abre un adjunto en una pestaña nueva descargando el blob primero.
   * Esto evita problemas con:
   *  - navegadores configurados para descargar PDFs en vez de previsualizarlos
   *  - bloqueadores de popups (abrimos `about:blank` dentro del click gesture)
   *  - exposición del token en la URL
   */
  const openAttachment = async (att) => {
    const newWin = window.open("", "_blank");
    try {
      const res = await api.get(
        `/expedientes/${expedienteId}/attachments/${att.id}/file`,
        { params: { token }, responseType: "blob" }
      );
      const mime = att.mime_type || res.headers["content-type"] || "application/pdf";
      const blob = new Blob([res.data], { type: mime });
      const url = URL.createObjectURL(blob);
      if (newWin && !newWin.closed) {
        newWin.location.href = url;
      } else {
        // Popup bloqueado → forzamos descarga
        const a = document.createElement("a");
        a.href = url;
        a.download = att.filename || "documento";
        document.body.appendChild(a);
        a.click();
        a.remove();
      }
      setTimeout(() => URL.revokeObjectURL(url), 60_000);
    } catch (e) {
      if (newWin && !newWin.closed) newWin.close();
      toast.error("No se pudo abrir el documento");
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [expedienteId]);

  const upload = async (files) => {
    if (!files?.length) return;
    setUploading(true);
    try {
      const fd = new FormData();
      Array.from(files).forEach((f) => fd.append("files", f));
      fd.append("kind", "doc");
      const { data } = await api.post(
        `/expedientes/${expedienteId}/attachments/upload`,
        fd,
        {
          headers: { "Content-Type": "multipart/form-data" },
          timeout: 5 * 60 * 1000,
        },
      );
      const ok = data?.created || 0;
      const ko = data?.failed?.length || 0;
      if (ko > 0) {
        const names = data.failed.map((f) => f.filename).slice(0, 5).join(", ");
        toast.error(
          `Se subieron ${ok} de ${ok + ko}. Reintenta: ${names}${ko > 5 ? "…" : ""}`,
          { duration: 8000 },
        );
      } else if (ok > 0) {
        toast.success(`${ok} documento${ok === 1 ? "" : "s"} subido${ok === 1 ? "" : "s"}`);
      }
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error subiendo documentos");
    } finally {
      setUploading(false);
    }
  };

  const onDrop = (e) => {
    e.preventDefault();
    setDrag(false);
    upload(e.dataTransfer.files);
  };

  const del = async (id) => {
    if (!confirm("¿Eliminar este documento?")) return;
    try {
      await api.delete(`/expedientes/${expedienteId}/attachments/${id}`);
      load();
    } catch (e) {
      alert(e?.response?.data?.detail || "Error");
    }
  };

  const toggleInforme = async (it) => {
    const next = it.folder === "informe" ? null : "informe";
    try {
      await api.patch(`/expedientes/${expedienteId}/attachments/${it.id}`, { folder: next });
      load();
      // Re-cargar a los 7s para reflejar la categoría detectada por la IA
      if (next === "informe" && !it.categoria) {
        setTimeout(load, 7000);
      }
    } catch (e) {
      alert(e?.response?.data?.detail || "Error");
    }
  };

  const reclassify = async (id) => {
    try {
      await api.post(`/expedientes/${expedienteId}/attachments/${id}/classify`);
      load();
    } catch (e) {
      alert(e?.response?.data?.detail || "No se pudo reclasificar");
    }
  };

  /**
   * Marca / desmarca un adjunto como "Consulta Descriptiva y Gráfica de
   * datos catastrales". Cuando está marcado, el generador de informe
   * lo colocará dentro del bloque catastral (Descripción del riesgo)
   * en lugar del anexo documental final.
   *
   * Overridea la heurística automática por nombre de archivo.
   */
  const toggleConsultaCatastral = async (it) => {
    const next = it.is_consulta_catastral === true ? null : true;
    try {
      await api.patch(`/expedientes/${expedienteId}/attachments/${it.id}`, {
        is_consulta_catastral: next,
      });
      toast.success(
        next === true
          ? "Marcado como Consulta Catastral"
          : "Se aplica la detección automática",
      );
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudo marcar");
    }
  };

  // Agrupa: 'informe' primero, luego 'Emails indexados' y luego el resto.
  // El resto cubre tanto los documentos sin carpeta como los informes
  // generados automáticamente (folder `informe_pdf`, `informe_docx`,
  // `informe_preliminar_*`, `informe_final`…) — estos se muestran en
  // "Documentos generales" con su log "Generado el …".
  // Las carpetas personalizadas del usuario se gestionan abajo en el
  // FolderBrowser.
  const enInforme = items.filter((i) => i.folder === "informe");
  const emails = items.filter((i) => i.folder === "Emails indexados");
  const inCustom = items.filter(
    (i) => i.folder && userCustomFolders.some((f) => i.folder === f || (i.folder || "").startsWith(`${f}/`))
  );
  const general = items.filter(
    (i) =>
      i.folder !== "informe"
      && i.folder !== "Emails indexados"
      && !inCustom.includes(i)
  );

  const createCustomFolder = async () => {
    const name = window.prompt("Nombre de la nueva carpeta:");
    if (!name?.trim()) return;
    try {
      await api.post(`/expedientes/${expedienteId}/folders`, {
        kind: "doc", path: name.trim(),
      });
      toast.success(`Carpeta "${name.trim()}" creada`);
      loadFolders();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudo crear");
    }
  };

  const totalSize = items.reduce((a, i) => a + (i.size_bytes || 0), 0);

  return (
    <div className="h-full overflow-y-auto bg-white">
      <div className="p-6 max-w-5xl">
        {/* Top bar */}
        <div
          onDragOver={(e) => { e.preventDefault(); setDrag(true); }}
          onDragLeave={() => setDrag(false)}
          onDrop={onDrop}
          onClick={() => inputRef.current?.click()}
          className={`border-2 border-dashed rounded-lg p-6 cursor-pointer transition-all flex items-center gap-4 ${
            drag ? "border-foreground bg-surface-2" : "border-border bg-surface hover:bg-surface-2"
          }`}
          data-testid="docs-dropzone"
        >
          <UploadSimple className="w-7 h-7 text-muted flex-shrink-0" weight="bold" />
          <div className="flex-1">
            <div className="font-semibold text-sm">Subir documentos</div>
            <div className="text-xs text-muted">PDFs, Word, Excel, imágenes... Arrastra aquí o haz clic.</div>
          </div>
          {uploading && <div className="dot-blink text-muted"><span/><span/><span/></div>}
          <input
            ref={inputRef}
            type="file"
            multiple
            onChange={(e) => upload(e.target.files)}
            className="hidden"
            data-testid="docs-file-input"
          />
        </div>

        {/* Header info */}
        <div className="mt-5 flex items-baseline justify-between">
          <h2 className="text-sm">
            <span className="font-semibold">{items.length} documento{items.length === 1 ? "" : "s"}</span>
            <span className="text-muted ml-2">· {fmtSize(totalSize)}</span>
            {enInforme.length > 0 && (
              <span className="ml-3 inline-flex items-center gap-1 text-[11px] bg-amber-50 text-amber-800 border border-amber-200 rounded-full px-2 py-0.5 font-semibold">
                <PaperclipHorizontal className="w-3 h-3" weight="bold" />
                {enInforme.length} en Anexo del informe
              </span>
            )}
          </h2>
          <button
            type="button"
            onClick={createCustomFolder}
            data-testid="docs-new-folder-btn"
            className="text-[11.5px] inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md border border-border bg-white hover:bg-background font-semibold"
          >
            <FolderOpen className="w-3.5 h-3.5" weight="duotone" />
            Nueva carpeta
          </button>
        </div>

        {/* Selection bar — sticky cuando hay archivos seleccionados */}
        {selected.size > 0 && (
          <div className="mt-3 bg-amber-50 border border-amber-200 rounded-md px-4 py-2.5 flex items-center justify-between gap-3 sticky top-0 z-10 shadow-sm" data-testid="selection-bar">
            <div className="text-xs text-amber-900">
              <strong>{selected.size}</strong> seleccionado{selected.size === 1 ? "" : "s"}
              <span className="opacity-70 ml-2">· {fmtSize(selectedSize)}</span>
              {selectedSize > 25 * 1024 * 1024 && (
                <span className="ml-2 text-rose-700 font-semibold">⚠️ supera 25 MB</span>
              )}
            </div>
            <div className="flex gap-2">
              <button
                onClick={clearSelection}
                className="text-xs px-3 py-1.5 text-amber-900 hover:bg-amber-100 rounded-md inline-flex items-center gap-1.5"
                data-testid="selection-clear"
              >
                <X className="w-3 h-3" weight="bold" /> Anular
              </button>
              <button
                onClick={sendSelected}
                className="text-xs px-3 py-1.5 bg-amber-600 hover:bg-amber-700 text-white rounded-md font-semibold inline-flex items-center gap-1.5"
                data-testid="selection-send-email"
              >
                <EnvelopeSimple className="w-3.5 h-3.5" weight="bold" />
                Enviar por email
              </button>
            </div>
          </div>
        )}

        {/* Carpeta INFORME (siempre visible) */}
        <FolderSection
          title="Carpeta «Informe» · Anexo documental"
          subtitle="Estos documentos se adjuntan automáticamente al final del Informe Final."
          accent="amber"
          items={enInforme}
          empty="Aún no has marcado ningún documento como «Anexo del informe». Pulsa el clip en cualquier documento de abajo."
          onToggle={toggleInforme}
          onDelete={del}
          onReclassify={reclassify}
          onToggleConsultaCatastral={toggleConsultaCatastral}
          onOpen={openAttachment}
          showCategory
          showCatastralToggle
          selected={selected}
          onToggleSelect={toggleSelect}
        />

        {/* Carpeta EMAILS INDEXADOS — sólo si hay alguno */}
        {emails.length > 0 && (
          <FolderSection
            title="Emails indexados"
            subtitle="Copia automática de los emails enviados desde este expediente."
            accent="slate"
            items={emails}
            onToggle={toggleInforme}
            onDelete={del}
            onReclassify={reclassify}
            onOpen={openAttachment}
            selected={selected}
            onToggleSelect={toggleSelect}
          />
        )}

        {/* Carpeta GENERAL */}
        <FolderSection
          title="Documentos generales"
          subtitle="Toda la documentación del expediente."
          accent="slate"
          items={general}
          empty="Sin documentos. Arrastra archivos arriba."
          onToggle={toggleInforme}
          onDelete={del}
          onReclassify={reclassify}
          onOpen={openAttachment}
          selected={selected}
          onToggleSelect={toggleSelect}
        />

        {/* Mis carpetas (custom) con drag&drop */}
        {showFolderBrowser && (
          <div className="mt-6" data-testid="docs-custom-folders">
            <div className="text-[11px] uppercase tracking-wider font-bold text-muted mb-2">
              Mis carpetas
            </div>
            <div className="bg-white border border-border rounded-lg p-3 min-h-[200px]">
              <FolderBrowser
                expedienteId={expedienteId}
                kind="doc"
                items={inCustom}
                onMoveItem={moveItem}
                renderGrid={(filtered, folder) =>
                  filtered.length === 0 ? (
                    <div className="text-[12.5px] text-muted italic text-center py-10">
                      {folder
                        ? "Carpeta vacía. Arrastra documentos aquí desde las secciones de arriba."
                        : "Selecciona una carpeta a la izquierda para ver su contenido."}
                    </div>
                  ) : (
                    <ul className="space-y-1.5">
                      {filtered.map((it) => {
                        const { Icon = FileText, color = "text-muted" } = ICONS[it.mime_type] || {};
                        return (
                          <li
                            key={it.id}
                            draggable
                            onDragStart={(e) => {
                              e.dataTransfer.setData("application/x-att-id", it.id);
                              e.dataTransfer.effectAllowed = "move";
                            }}
                            className="flex items-center gap-2 px-2 py-1.5 rounded-md border border-border bg-white cursor-move hover:bg-background"
                            data-testid={`docs-custom-row-${it.id}`}
                          >
                            <Icon className={`w-4 h-4 ${color}`} weight="duotone" />
                            <button
                              type="button"
                              onClick={() => openAttachment(it)}
                              className="flex-1 min-w-0 text-left text-[12.5px] truncate hover:underline"
                            >
                              {it.filename}
                            </button>
                            <span className="text-[10.5px] text-muted">{fmtSize(it.size_bytes)}</span>
                            <button
                              type="button"
                              onClick={() => del(it.id)}
                              className="text-muted hover:text-rose-700 p-0.5"
                              title="Eliminar"
                            >
                              <Trash className="w-3.5 h-3.5" weight="bold" />
                            </button>
                          </li>
                        );
                      })}
                    </ul>
                  )
                }
              />
            </div>
            <div className="text-[10.5px] text-muted mt-2 italic">
              Tip: arrastra cualquier fila de las secciones de arriba (Anexo IA o Generales) hasta una carpeta para moverla.
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// =====================================================================
// Sección reutilizable por carpeta
// =====================================================================
function FolderSection({ title, subtitle, accent, items, empty, onToggle, onDelete, onReclassify, onOpen, showCategory = false, selected, onToggleSelect, onToggleConsultaCatastral, showCatastralToggle = false }) {
  const accentRing = accent === "amber"
    ? "border-amber-300 bg-amber-50/30"
    : "border-border bg-white";
  const accentTitle = accent === "amber" ? "text-amber-800" : "text-foreground";
  const accentIcon = accent === "amber" ? "text-amber-700" : "text-muted";

  return (
    <div className={`mt-4 border rounded-md overflow-hidden ${accentRing}`}>
      <div className="px-4 py-2.5 flex items-center gap-2 border-b border-inherit">
        <FolderOpen className={`w-4 h-4 ${accentIcon}`} weight="fill" />
        <div className="flex-1 min-w-0">
          <div className={`font-semibold text-[13px] ${accentTitle}`}>{title}</div>
          <div className="text-[11px] text-muted">{subtitle}</div>
        </div>
        <span className="text-[11px] text-muted">{items.length} archivo{items.length === 1 ? "" : "s"}</span>
      </div>
      <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <tbody>
          {items.length === 0 && (
            <tr><td className="py-5 text-center text-muted text-[12px]">{empty}</td></tr>
          )}
          {items.map((it) => {
            const { Icon = FileText, color = "text-muted" } = ICONS[it.mime_type] || {};
            const isParte = it.kind === "parte";
            const inInforme = it.folder === "informe";
            const cat = it.categoria || null;
            const catLabel = cat ? CATEGORIA_LABEL[cat] || "Otros" : null;
            const catColor = cat ? CATEGORIA_COLOR[cat] || CATEGORIA_COLOR.otro : "";
            return (
              <tr key={it.id}
                  draggable
                  onDragStart={(e) => {
                    e.dataTransfer.setData("application/x-att-id", it.id);
                    e.dataTransfer.effectAllowed = "move";
                  }}
                  className={`border-t border-border/60 hover:bg-surface/40 align-top cursor-move ${selected?.has(it.id) ? "bg-amber-50/40" : ""}`}
                  data-testid={`doc-row-${it.id}`}>
                <td className="py-2.5 pl-3 pr-1 w-8">
                  <input
                    type="checkbox"
                    checked={!!selected?.has(it.id)}
                    onChange={() => onToggleSelect?.(it.id)}
                    className="rounded border-border cursor-pointer"
                    aria-label={`Seleccionar ${it.filename}`}
                    data-testid={`doc-select-${it.id}`}
                  />
                </td>
                <td className="py-2.5 px-4">
                  <button
                    type="button"
                    onClick={() => onOpen?.(it)}
                    className="inline-flex items-center gap-2 hover:underline text-left"
                    data-testid={`open-doc-${it.id}`}
                  >
                    <Icon className={`w-4 h-4 ${color}`} weight="duotone" />
                    <span className="truncate max-w-[420px]">{it.titulo_ia || it.filename}</span>
                    {isParte && <Star className="w-3 h-3 text-status-warning-fg" weight="fill" />}
                  </button>
                  {it.kind === "informe" && it.created_at && (
                    <div
                      className="text-[11px] text-muted mt-0.5 ml-6"
                      data-testid={`informe-generated-log-${it.id}`}
                    >
                      Generado el {new Date(it.created_at).toLocaleDateString("es-ES", {
                        day: "2-digit", month: "2-digit", year: "numeric",
                      })} a las {new Date(it.created_at).toLocaleTimeString("es-ES", {
                        hour: "2-digit", minute: "2-digit",
                      })}
                    </div>
                  )}
                  {showCategory && it.resumen_ia && (
                    <div className="text-[11px] text-muted mt-0.5 ml-6 max-w-[520px]">{it.resumen_ia}</div>
                  )}
                </td>
                <td className="py-2.5 px-2 w-32">
                  {showCategory && catLabel ? (
                    <span className={`text-[10px] px-2 py-0.5 rounded-full font-medium border ${catColor}`} data-testid={`category-${it.id}`}>
                      {catLabel}
                    </span>
                  ) : (
                    <span className={`text-[10px] px-2 py-0.5 rounded-full font-medium ${isParte ? "bg-status-warning-bg text-status-warning-fg border border-status-warning-bd" : "bg-surface text-muted"}`}>
                      {isParte ? "Parte" : it.kind}
                    </span>
                  )}
                </td>
                <td className="py-2.5 px-2 text-muted text-[12px] whitespace-nowrap w-28">{fmtDate(it.created_at)}</td>
                <td className="py-2.5 px-2 text-right text-muted text-[12px] whitespace-nowrap w-20">{fmtSize(it.size_bytes)}</td>
                <td className="py-2.5 px-2 text-right whitespace-nowrap">
                  {showCategory && (
                    <button
                      onClick={() => onReclassify(it.id)}
                      title="Reclasificar con IA"
                      data-testid={`reclassify-${it.id}`}
                      className="inline-flex items-center gap-1 text-[10px] text-violet-700 hover:bg-violet-50 rounded-md px-1.5 py-1 mr-1 border border-transparent hover:border-violet-200"
                    >
                      <Sparkle className="w-3 h-3" weight="bold" />
                    </button>
                  )}
                  {showCatastralToggle && it.mime_type === "application/pdf" && (
                    <button
                      onClick={() => onToggleConsultaCatastral?.(it)}
                      title={
                        it.is_consulta_catastral === true
                          ? "Desmarcar como Consulta Catastral (volverá al anexo documental)"
                          : "Marcar como Consulta Descriptiva y Gráfica del Catastro (irá al bloque catastral del informe)"
                      }
                      data-testid={`toggle-catastral-${it.id}`}
                      className={`inline-flex items-center gap-1 text-[10px] font-semibold rounded-md px-1.5 py-1 mr-1 border ${
                        it.is_consulta_catastral === true
                          ? "bg-orange-600 text-white border-orange-600 hover:bg-orange-700"
                          : "bg-white text-orange-700 border-orange-200 hover:bg-orange-50"
                      }`}
                    >
                      <MapPin className="w-3 h-3" weight="bold" />
                      {it.is_consulta_catastral === true ? "Catastral" : "Es catastral"}
                    </button>
                  )}
                  <button
                    onClick={() => onToggle(it)}
                    title={inInforme ? "Quitar del Anexo del informe" : "Anexar al informe"}
                    data-testid={`toggle-informe-${it.id}`}
                    className={`inline-flex items-center gap-1 text-[11px] font-semibold rounded-md px-2 py-1 mr-1 border ${
                      inInforme
                        ? "bg-amber-500 text-white border-amber-500 hover:bg-amber-600"
                        : "bg-white text-amber-700 border-amber-300 hover:bg-amber-50"
                    }`}
                  >
                    <PaperclipHorizontal className="w-3 h-3" weight="bold" />
                    {inInforme ? "En informe" : "Anexar"}
                  </button>
                  {!isParte && (
                    <button onClick={() => onDelete(it.id)} className="p-1 text-muted hover:text-status-error-fg" data-testid={`delete-doc-${it.id}`}>
                      <Trash className="w-3.5 h-3.5" weight="bold" />
                    </button>
                  )}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
      </div>
    </div>
  );
}
